import { Button, Chip, DrinkIcon, formatBaht, Icons } from "@jod/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { track } from "../../features/analytics/track.ts";
import {
  CUSTOMER_DEMO_VENUE_ID,
  customerBackendApi,
  menuItemFromDto,
} from "../../features/api/backend.ts";
import { useT } from "../../features/i18n/index.ts";
import { useSession } from "../../features/session/session-context.tsx";
import { MENU_CATEGORIES, type MockMenuItem, mockMenu } from "../../mocks/data.ts";

export const Route = createFileRoute("/_tabs/menu")({
  component: MenuScreen,
});

function MenuScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { addToCart, cartCount, cartTotalMinor, toast } = useSession();
  const [cat, setCat] = useState<(typeof MENU_CATEGORIES)[number]>("Recommended");
  const backendEnabled = customerBackendApi !== null;
  const menuQuery = useQuery({
    queryKey: ["customer", "menu", CUSTOMER_DEMO_VENUE_ID],
    queryFn: () =>
      customerBackendApi?.getMenu(CUSTOMER_DEMO_VENUE_ID) ??
      Promise.reject(new Error("Customer backend API unavailable")),
    enabled: backendEnabled,
  });

  const sourceMenu = backendEnabled ? (menuQuery.data?.items.map(menuItemFromDto) ?? []) : mockMenu;
  const items = sourceMenu.filter((m) =>
    cat === "Recommended" ? m.recommended : m.category === cat,
  );

  // AEV-008 menu_viewed: fires once per category shown, once the list itself
  // has content (skips the loading/empty flash while the backend query settles).
  useEffect(() => {
    if (items.length === 0) return;
    track("menu_viewed", { category_ref: cat, item_count: items.length });
  }, [cat, items.length]);

  const add = (item: MockMenuItem) => {
    if (item.isAvailable === false) return;
    addToCart(
      {
        id: item.id,
        name: item.name,
        nameTh: item.nameTh,
        priceMinor: item.priceMinor,
        iconName: item.iconName,
      },
      1,
    );
    toast(t("menu.addedToast", { item: item.nameTh }));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <div className="jod-kicker">{t("menu.kicker")}</div>
        <h1 className="h-display text-[1.5rem]">{t("menu.title")}</h1>
      </div>

      {/* category rail */}
      <div className="-mx-[18px] flex gap-2 overflow-x-auto px-[18px] pb-1 [scrollbar-width:none]">
        {MENU_CATEGORIES.map((c) => (
          <Chip key={c} on={cat === c} onClick={() => setCat(c)} className="shrink-0">
            {c}
          </Chip>
        ))}
      </div>

      {backendEnabled && menuQuery.isLoading ? (
        <p className="rounded-lg border border-hairline bg-glass px-3 py-2 text-cream-3 text-xs">
          {t("menu.loading")}
        </p>
      ) : null}
      {backendEnabled && menuQuery.isError ? (
        <p className="rounded-lg bg-red-500/10 px-3 py-2 text-red-100 text-xs">
          {t("menu.loadError")}
        </p>
      ) : null}

      <div className="space-y-2.5">
        {items.map((item) => {
          const soldOut = item.isAvailable === false;
          return (
            <div
              key={item.id}
              className={`glass flex items-center gap-3.5 rounded-lg p-3 ${soldOut ? "opacity-50" : ""}`}
            >
              <span className="grid size-14 shrink-0 place-items-center rounded-lg bg-[oklch(100%_0_0_/_5%)] text-gold">
                <DrinkIcon name={item.iconName} size={28} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="truncate font-display font-semibold text-cream">
                    {item.name}
                  </span>
                  {item.recommended ? (
                    <span className="text-gold">
                      <Icons.IconSparkle size={13} />
                    </span>
                  ) : null}
                  {item.alcohol ? (
                    <span className="rounded-full border border-hairline bg-[oklch(100%_0_0_/_6%)] px-1.5 py-0.5 text-[0.6rem] text-cream-3">
                      {t("menu.alcoholBadge")}
                    </span>
                  ) : null}
                  {soldOut ? (
                    <span className="rounded-full bg-[oklch(100%_0_0_/_8%)] px-1.5 py-0.5 text-[0.6rem] text-cream-3">
                      {t("menu.soldOutBadge")}
                    </span>
                  ) : null}
                </div>
                <div className="truncate text-cream-3 text-xs">{item.nameTh}</div>
                <div className="mt-0.5 font-semibold text-gold text-sm">
                  {formatBaht(item.priceMinor)}
                </div>
              </div>
              <Button size="sm" variant="ghost" disabled={soldOut} onClick={() => add(item)}>
                <Icons.IconPlus size={15} /> {soldOut ? t("menu.soldOut") : t("menu.add")}
              </Button>
            </div>
          );
        })}
      </div>

      {/* sticky cart bar */}
      {cartCount > 0 ? (
        <div className="fixed inset-x-3 bottom-[6.2rem] z-30 mx-auto max-w-[calc(28rem-1.5rem)]">
          <Button className="w-full justify-between" onClick={() => navigate({ to: "/cart" })}>
            <span className="flex items-center gap-2">
              <Icons.IconMenuGlyph size={18} /> {t("common.itemsCount", { count: cartCount })}
            </span>
            <span>
              {formatBaht(cartTotalMinor)} · {t("menu.viewCart")}
            </span>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
